import { AddToCartMutation } from '@app/data/shop-bff';
import ButtonToggle from '@redbubble/design-system/src/ButtonToggle';
import * as constants from '@redbubble/design-system/src/constants';
import CartAddIcon from '@redbubble/design-system/src/Icons/CartAdd';
import TickIcon from '@redbubble/design-system/src/Icons/Tick';
import { useViewportWidth } from '@redbubble/next/hooks';
import { DeviceType } from '@redbubble/next/types';
import { useState } from 'react';
import { ObjectValues } from 'types';

const buttonStatus = {
  SUCCESS: 'success',
  INITIAL: 'initial',
  LOADING: 'loading',
} as const;

export const embeddedAddToCartStyleVariant = {
  DEFAULT: 'default',
  SEARCH_RESULTS_PAGE_PRODUCT_CARD: 'searchResultsPageProductCard',
} as const;

type ButtonStatus = ObjectValues<typeof buttonStatus>;
type Variant = ObjectValues<typeof embeddedAddToCartStyleVariant>;

export type EmbeddedAddToCartCallback = (
  itemId: string,
  index: number
) => Promise<AddToCartMutation['shop_addToCart'] | null>;

interface EmbeddedAddToCartButtonProps {
  id: string;
  addItemToCart: EmbeddedAddToCartCallback;
  notify: (event) => void;
  index: number;
  variant?: Variant;
}

export const EmbeddedAddToCartButton: React.FC<
  EmbeddedAddToCartButtonProps
> = ({
  id,
  addItemToCart,
  notify,
  index,
  variant = embeddedAddToCartStyleVariant.DEFAULT,
}) => {
  const { isOrAbove } = useViewportWidth();
  const isMobile = !isOrAbove(DeviceType.Tablet);
  const [status, setStatus] = useState<ButtonStatus>('initial');

  const handleAddToCartClick = async (event) => {
    event.preventDefault();
    setStatus('loading');

    const shopAddToCart = await addItemToCart(id, index);

    if (shopAddToCart) {
      setStatus('success');
      notify(event);
      setTimeout(() => {
        setStatus('initial');
      }, 1000);
    }
  };

  const buttonStyle =
    variant === embeddedAddToCartStyleVariant.SEARCH_RESULTS_PAGE_PRODUCT_CARD
      ? {
          borderColor: isMobile
            ? 'var(--ds-color-neutral-200)'
            : 'var(--ds-color-neutral-300)',
        }
      : undefined;

  return (
    <ButtonToggle
      data-testid="embedded-add-to-cart-button"
      active={status === 'success'}
      loading={status === 'loading'}
      circle
      onClick={(event) => handleAddToCartClick(event)}
      size={constants.SMALL}
      iconAfter={
        {
          success: <TickIcon size={constants.MEDIUM} color="#FFFFFF" />,
          initial: <CartAddIcon size={constants.SMALL} />,
          loading: null,
        }[status]
      }
      // We must use style prop here as the className prop is not supported by ButtonToggle
      // which is a proxy for the Button component. It does not get merged with styles
      // provided by the design system Button component.
      // Css could have been used to apply the styles but didnt want to add a dom element
      // to wrap the ButtonToggle component.
      // This should be implemented correctly should experiment SE2508 is successful.
      style={buttonStyle}
      aria-label="Add to cart"
    />
  );
};
